import routes from '../../routes';
import { bandittoApi } from '../../store/api/api';
import { PaginationRequest } from '../../type';
import {
  ArtifactType,
  CreateModelType,
  CreateStylePromptType,
  CreateStyleType,
  ModelCatalogueType,
  ModelPriceType,
  ModelType,
  StyleListItemType,
  StylePromptType,
  StyleType,
  ToolType,
  UpdateModelPriceType,
  UpdateModelType,
  UpdateStyleType,
  UpdateToolType,
} from './tools.types';

type SuccessResponse = {
  success: boolean;
};

type GetToolsRequest = PaginationRequest & {
  integrationName?: string;
};

type GetToolsResponse = {
  countAll: number;
  tools: ToolType[];
};

type GetArtifactsRequest = Partial<PaginationRequest> & {
  consumerId?: string;
  toolId?: string;
  modelId?: string;
  style?: string;
  published?: boolean;
  startDate?: string;
  endDate?: string;
  prompt?: string;
};

type GetArtifactsResponse = {
  countAll: number;
  artifacts: ArtifactType[];
};

type GetStylesRequest = Partial<PaginationRequest> & {
  toolId?: string;
  modelId?: string;
  name?: string;
  deleted?: boolean;
};

type GetStylesResponse = {
  countAll: number;
  styles: StyleListItemType[];
};

type GetStylePromptsRequest = Partial<PaginationRequest> & {
  styleId: string;
  toolId?: string;
  modelId?: string;
  deleted?: boolean;
};

type GetStylePromptsResponse = {
  countAll: number;
  prompts: StylePromptType[];
};

type GetModelsResponse = {
  countAll: number;
  models: ModelType[];
};

type GetModelPricesResponse = {
  countAll: number;
  prices: ModelPriceType[];
};

const toolsApi = bandittoApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllTools: builder.query<GetToolsResponse, GetToolsRequest>({
      query: (params) => ({
        url: routes.api.tools(),
        params,
      }),
      providesTags: ['Tools'],
    }),
    getToolById: builder.query<ToolType, string>({
      query: (toolId) => routes.api.tool(toolId),
      providesTags: ['Tool'],
    }),
    updateTool: builder.mutation<ToolType, { id: string; tool: UpdateToolType }>({
      query: ({ id, tool }) => ({
        url: routes.api.tool(id),
        method: 'PUT',
        body: tool,
      }),
      invalidatesTags: ['Tool', 'Tools'],
    }),
    enableOrDisableToolSwitcher: builder.mutation<
      ToolType,
      { toolId: string; switcher: 'enable' | 'disable' }
    >({
      query: ({ toolId, switcher }) => ({
        url: routes.api.enableOrDisableTool(toolId, switcher),
        method: 'PATCH',
      }),
      invalidatesTags: ['Tool', 'Tools'],
    }),
    getAllArtifacts: builder.query<GetArtifactsResponse, GetArtifactsRequest>({
      query: (params) => ({
        url: routes.api.artifacts(),
        params,
      }),
      providesTags: ['Artifacts'],
    }),
    publishOrUnpublishArtifact: builder.mutation<
      SuccessResponse,
      { artifactId: string; switcher: 'publish' | 'unpublish' }
    >({
      query: ({ artifactId, switcher }) => ({
        url: routes.api.publishOrUnpublishArtifact(artifactId, switcher),
        method: 'PATCH',
      }),
      invalidatesTags: ['Artifacts'],
    }),
    getAllStyles: builder.query<GetStylesResponse, GetStylesRequest>({
      query: (params) => ({
        url: routes.api.styles(),
        params,
      }),
      providesTags: ['Styles'],
    }),
    getStyleById: builder.query<StyleType, string>({
      query: (styleId) => routes.api.style(styleId),
      providesTags: ['Style'],
    }),
    createStyle: builder.mutation<StyleType, CreateStyleType>({
      query: (style) => ({
        url: routes.api.styles(),
        method: 'POST',
        body: style,
      }),
      invalidatesTags: ['Styles'],
    }),
    updateStyle: builder.mutation<StyleType, { styleId: string; style: UpdateStyleType }>({
      query: ({ styleId, style }) => ({
        url: routes.api.style(styleId),
        method: 'PATCH',
        body: style,
      }),
      invalidatesTags: ['Style', 'Styles'],
    }),
    deleteStyle: builder.mutation<SuccessResponse, string>({
      query: (styleId) => ({
        url: routes.api.style(styleId),
        method: 'DELETE',
      }),
      invalidatesTags: ['Style', 'Styles'],
    }),
    restoreStyle: builder.mutation<SuccessResponse, string>({
      query: (styleId) => ({
        url: routes.api.restoreStyle(styleId),
        method: 'PATCH',
      }),
      invalidatesTags: ['Style', 'Styles'],
    }),
    getStylePrompts: builder.query<GetStylePromptsResponse, GetStylePromptsRequest>({
      query: ({ styleId, ...params }) => ({
        url: routes.api.stylePrompts(styleId),
        params,
      }),
      providesTags: ['StylePrompts'],
    }),
    createStylePrompt: builder.mutation<
      StylePromptType,
      { styleId: string; prompt: CreateStylePromptType }
    >({
      query: ({ styleId, prompt }) => ({
        url: routes.api.stylePrompts(styleId),
        method: 'POST',
        body: prompt,
      }),
      // a style carries a snapshot of one prompt, so a new version changes the style too
      invalidatesTags: ['StylePrompts', 'Style', 'Styles'],
    }),
    publishStylePrompt: builder.mutation<StylePromptType, string>({
      query: (promptId) => ({
        url: routes.api.publishStylePrompt(promptId),
        method: 'PATCH',
      }),
      invalidatesTags: ['StylePrompts', 'Style', 'Styles'],
    }),
    deleteStylePrompt: builder.mutation<SuccessResponse, string>({
      query: (promptId) => ({
        url: routes.api.stylePrompt(promptId),
        method: 'DELETE',
      }),
      invalidatesTags: ['StylePrompts', 'Style', 'Styles'],
    }),
    getAllModels: builder.query<GetModelsResponse, Partial<PaginationRequest>>({
      query: (params) => ({
        url: routes.api.models(),
        params,
      }),
      providesTags: ['Models'],
    }),
    getModelCatalogue: builder.query<ModelCatalogueType, void>({
      query: () => routes.api.modelCatalogue(),
    }),
    getModelById: builder.query<ModelType, string>({
      query: (modelId) => routes.api.model(modelId),
      providesTags: ['Model'],
    }),
    createModel: builder.mutation<ModelType, CreateModelType>({
      query: (model) => ({
        url: routes.api.models(),
        method: 'POST',
        body: model,
      }),
      invalidatesTags: ['Models'],
    }),
    updateModel: builder.mutation<ModelType, { modelId: string; model: UpdateModelType }>({
      query: ({ modelId, model }) => ({
        url: routes.api.model(modelId),
        method: 'PATCH',
        body: model,
      }),
      invalidatesTags: ['Model', 'Models'],
    }),
    deleteModel: builder.mutation<SuccessResponse, string>({
      query: (modelId) => ({
        url: routes.api.model(modelId),
        method: 'DELETE',
      }),
      invalidatesTags: ['Model', 'Models'],
    }),
    restoreModel: builder.mutation<SuccessResponse, string>({
      query: (modelId) => ({
        url: routes.api.restoreModel(modelId),
        method: 'PATCH',
      }),
      invalidatesTags: ['Model', 'Models'],
    }),
    getModelPrices: builder.query<
      GetModelPricesResponse,
      { modelId: string } & Partial<PaginationRequest>
    >({
      query: ({ modelId, ...params }) => ({
        url: routes.api.modelPrices(modelId),
        params,
      }),
      providesTags: ['ModelPrices'],
    }),
    getToolPrices: builder.query<
      GetModelPricesResponse,
      { toolId: string } & Partial<PaginationRequest>
    >({
      query: ({ toolId, ...params }) => ({
        url: routes.api.toolPrices(toolId),
        params,
      }),
      providesTags: ['ModelPrices'],
    }),
    getModelPriceByTool: builder.query<ModelPriceType, { toolId: string; modelId: string }>({
      query: ({ toolId, modelId }) => routes.api.modelPriceByTool(toolId, modelId),
      providesTags: ['ModelPrices'],
    }),
    createModelPrice: builder.mutation<
      ModelPriceType,
      { toolId: string; modelId: string; price: UpdateModelPriceType }
    >({
      query: ({ toolId, modelId, price }) => ({
        url: routes.api.modelPriceByTool(toolId, modelId),
        method: 'POST',
        body: price,
      }),
      invalidatesTags: ['ModelPrices'],
    }),
    updateModelPrice: builder.mutation<
      ModelPriceType,
      { toolId: string; modelId: string; price: UpdateModelPriceType }
    >({
      query: ({ toolId, modelId, price }) => ({
        url: routes.api.modelPriceByTool(toolId, modelId),
        method: 'PATCH',
        body: price,
      }),
      invalidatesTags: ['ModelPrices'],
    }),
    deleteModelPrice: builder.mutation<SuccessResponse, { toolId: string; modelId: string }>({
      query: ({ toolId, modelId }) => ({
        url: routes.api.modelPriceByTool(toolId, modelId),
        method: 'DELETE',
      }),
      invalidatesTags: ['ModelPrices'],
    }),
  }),
});

export const {
  useGetAllToolsQuery,
  useGetToolByIdQuery,
  useUpdateToolMutation,
  useEnableOrDisableToolSwitcherMutation,
  useGetAllArtifactsQuery,
  usePublishOrUnpublishArtifactMutation,
  useGetAllStylesQuery,
  useGetStyleByIdQuery,
  useCreateStyleMutation,
  useUpdateStyleMutation,
  useDeleteStyleMutation,
  useRestoreStyleMutation,
  useGetStylePromptsQuery,
  useCreateStylePromptMutation,
  usePublishStylePromptMutation,
  useDeleteStylePromptMutation,
  useGetAllModelsQuery,
  useGetModelCatalogueQuery,
  useGetModelByIdQuery,
  useCreateModelMutation,
  useUpdateModelMutation,
  useDeleteModelMutation,
  useRestoreModelMutation,
  useGetModelPricesQuery,
  useGetToolPricesQuery,
  useGetModelPriceByToolQuery,
  useCreateModelPriceMutation,
  useUpdateModelPriceMutation,
  useDeleteModelPriceMutation,
} = toolsApi;
